import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { SortingState } from '@tanstack/react-table';
import { getSearchParams, updateSearchParams } from '@/common/utils';
import { LocalStorageAcceptableKeys } from '@/common/types';
import { ColumnDef } from '@tanstack/react-table';

type useTanTableSortingProps = {
  columns: ColumnDef<any, any>[];
  localStorageKey?: LocalStorageAcceptableKeys;
  defaultSortingKey?: string;
  defaultSortingDirection?: 'asc' | 'desc';
};

export const useTanTableSorting = ({
  columns,
  localStorageKey,
  defaultSortingKey,
  defaultSortingDirection,
}: useTanTableSortingProps) => {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);

  const getSortParamName = useCallback(
    () => (localStorageKey ? `sort_${localStorageKey}` : 'sort'),
    [localStorageKey],
  );
  const getDirectionParamName = useCallback(
    () => (localStorageKey ? `direction_${localStorageKey}` : 'direction'),
    [localStorageKey],
  );

  const columnIds = useMemo(
    () =>
      columns
        .map((col) => col.id || (col as any).accessorKey)
        .filter(Boolean) as string[],
    [columns],
  );

  const getFallbackKey = useCallback(() => {
    return defaultSortingKey && columnIds.includes(defaultSortingKey)
      ? defaultSortingKey
      : columnIds[0];
  }, [defaultSortingKey, columnIds]);

  // Helper function to initialize sorting from URL or localStorage
  const initializeSorting = useCallback(() => {
    if (columnIds.length === 0) return null;

    let initialSorting: SortingState | null = null;

    // Only read from URL/localStorage if localStorageKey is provided
    if (localStorageKey) {
      const sortParamName = getSortParamName();
      const directionParamName = getDirectionParamName();
      const urlSort = getSearchParams(sortParamName);
      const urlDirection = getSearchParams(directionParamName) as
        | 'asc'
        | 'desc'
        | null;

      // Priority: URL > localStorage > defaultSortingKey/first column
      if (urlSort && columnIds.includes(urlSort)) {
        // URL takes precedence
        initialSorting = [
          {
            id: urlSort,
            desc: urlDirection === 'desc',
          },
        ];

        localStorage.updateObjectProperty(localStorageKey, 'sortingObject', {
          key: urlSort,
          direction: urlDirection || 'asc',
        });
      } else {
        // Try localStorage
        const localStorageValue = localStorage.getObject<
          { sortingObject?: { key: string; direction: 'asc' | 'desc' } },
          typeof localStorageKey
        >(localStorageKey);

        if (localStorageValue?.sortingObject) {
          const storedSort = localStorageValue.sortingObject;

          // Check if stored column still exists
          if (columnIds.includes(storedSort.key)) {
            initialSorting = [
              {
                id: storedSort.key,
                desc: storedSort.direction === 'desc',
              },
            ];

            // Update URL with localStorage values
            updateSearchParams(sortParamName, storedSort.key, true, navigate);
            updateSearchParams(
              directionParamName,
              storedSort.direction,
              true,
              navigate,
            );
          } else {
            // Column doesn't exist anymore (Column customization) - sort by defaultSortingKey or first column but don't update localStorage
            const fallbackKey = getFallbackKey();
            if (fallbackKey) {
              initialSorting = [
                {
                  id: fallbackKey,
                  desc: defaultSortingDirection === 'desc',
                },
              ];
            }
          }
        }
      }
    }

    // If no sorting found yet, use default (works for both with and without localStorageKey)
    if (!initialSorting) {
      const defaultKey = getFallbackKey();
      if (defaultKey) {
        initialSorting = [
          {
            id: defaultKey,
            desc: defaultSortingDirection === 'desc',
          },
        ];

        // Update URL and localStorage only if localStorageKey is provided
        if (localStorageKey) {
          const sortParamName = getSortParamName();
          const directionParamName = getDirectionParamName();
          const defaultDirection = defaultSortingDirection || 'asc';

          updateSearchParams(sortParamName, defaultKey, true, navigate);
          updateSearchParams(
            directionParamName,
            defaultDirection,
            true,
            navigate,
          );
          localStorage.updateObjectProperty(localStorageKey, 'sortingObject', {
            key: defaultKey,
            direction: defaultDirection,
          });
        }
      }
    }

    return initialSorting;
  }, [
    localStorageKey,
    columnIds,
    navigate,
    getSortParamName,
    getDirectionParamName,
    getFallbackKey,
    defaultSortingDirection,
  ]);

  useLayoutEffect(() => {
    const initialSorting = initializeSorting();
    if (initialSorting) {
      setSorting(initialSorting);
    }
  }, [initializeSorting]);

  // Handle browser back button (only if localStorageKey is provided)
  useEffect(() => {
    if (!localStorageKey) return;

    const handlePopState = () => {
      const sortParamName = getSortParamName();
      const directionParamName = getDirectionParamName();
      const urlSort = getSearchParams(sortParamName);
      const urlDirection = getSearchParams(directionParamName) as
        | 'asc'
        | 'desc'
        | null;

      let newSorting: SortingState = [];

      if (urlSort && columnIds.includes(urlSort)) {
        // URL takes precedence
        newSorting = [
          {
            id: urlSort,
            desc: urlDirection === 'desc',
          },
        ];
      } else {
        // Try localStorage
        const localStorageValue = localStorage.getObject<
          {
            sortingObject?: {
              key: string;
              direction: 'asc' | 'desc';
            };
          },
          typeof localStorageKey
        >(localStorageKey);

        if (
          localStorageValue?.sortingObject &&
          columnIds.includes(localStorageValue.sortingObject.key)
        ) {
          // Restore from localStorage
          newSorting = [
            {
              id: localStorageValue.sortingObject.key,
              desc: localStorageValue.sortingObject.direction === 'desc',
            },
          ];
        }
      }

      // If no sorting found, fallback to defaultSortingKey or first column
      if (newSorting.length === 0 && columnIds.length > 0) {
        const fallbackKey = getFallbackKey();
        if (fallbackKey) {
          newSorting = [
            {
              id: fallbackKey,
              desc: defaultSortingDirection === 'desc',
            },
          ];
        }
      }

      if (newSorting.length > 0) {
        setSorting(newSorting);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [
    localStorageKey,
    columnIds,
    getSortParamName,
    getDirectionParamName,
    getFallbackKey,
    defaultSortingDirection,
  ]);

  // Handle sorting changes
  const handleSortingChange = useCallback(
    (updater: any) => {
      setSorting((prev) => {
        const newSorting =
          typeof updater === 'function' ? updater(prev) : updater;

        // If sorting is reset to empty (3rd state), apply default sorting.
        // When the default sort itself is toggled off, flip its direction instead.
        if (newSorting.length === 0) {
          const defaultKey = getFallbackKey();
          if (defaultKey) {
            const defaultDirection = defaultSortingDirection || 'asc';
            const previousSort = prev[0];
            const previousDirection = previousSort?.desc ? 'desc' : 'asc';
            const fallbackDirection =
              previousSort?.id === defaultKey &&
              previousDirection === defaultDirection
                ? defaultDirection === 'desc'
                  ? 'asc'
                  : 'desc'
                : defaultDirection;
            const defaultSorting: SortingState = [
              {
                id: defaultKey,
                desc: fallbackDirection === 'desc',
              },
            ];

            // Update URL and localStorage only if localStorageKey is provided
            if (localStorageKey) {
              const sortParamName = getSortParamName();
              const directionParamName = getDirectionParamName();

              updateSearchParams(sortParamName, defaultKey, true, navigate);
              updateSearchParams(
                directionParamName,
                fallbackDirection,
                true,
                navigate,
              );
              localStorage.updateObjectProperty(
                localStorageKey,
                'sortingObject',
                {
                  key: defaultKey,
                  direction: fallbackDirection,
                },
              );
            }

            return defaultSorting;
          }
        }

        // Update URL and localStorage only if localStorageKey is provided
        if (localStorageKey && newSorting.length > 0) {
          const sortItem = newSorting[0];
          const sortKey = sortItem.id;
          const sortDirection = sortItem.desc ? 'desc' : 'asc';
          const sortParamName = getSortParamName();
          const directionParamName = getDirectionParamName();

          updateSearchParams(sortParamName, sortKey, true, navigate);
          updateSearchParams(directionParamName, sortDirection, true, navigate);
          localStorage.updateObjectProperty(localStorageKey, 'sortingObject', {
            key: sortKey,
            direction: sortDirection,
          });
        }

        return newSorting;
      });
    },
    [
      localStorageKey,
      navigate,
      getSortParamName,
      getDirectionParamName,
      getFallbackKey,
      defaultSortingDirection,
    ],
  );

  return {
    sorting,
    handleSortingChange,
  };
};
